import * as Sentry from '@sentry/react'

enum HTTP_METHOD {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

type RequestStructure = {
  method: HTTP_METHOD
  path: string
  headers?: Record<string, string>
  body?: string | object
  signal?: AbortSignal
  suppressUnauthorizedRedirect?: boolean
}

/**
 * Auth0 error codes that mean the session is gone and the user must re-authenticate.
 * These surface when `getAccessTokenSilently` fails (e.g. missing/expired refresh token),
 * which is the primary way an expired session manifests — not as a backend 401.
 */
const REAUTH_ERROR_CODES = new Set([
  'login_required',
  'consent_required',
  'interaction_required',
  'missing_refresh_token',
  'invalid_grant',
])

/**
 * Messages browsers use when `fetch` never received a response — offline, dropped
 * connection, DNS/TLS failure, request killed while the tab was backgrounded. The
 * Fetch spec deliberately exposes nothing else, so there is no cause to act on and
 * no meaningful stack: the request simply never completed.
 */
const OPAQUE_FETCH_FAILURE_MESSAGES = new Set([
  'Failed to fetch', // Chromium
  'Load failed', // WebKit
  'NetworkError when attempting to fetch resource.', // Firefox
])

function isOpaqueNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError && OPAQUE_FETCH_FAILURE_MESSAGES.has(error.message)
}

function isReauthRequiredError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const { error: code, message } = error as { error?: unknown; message?: unknown }
  if (typeof code === 'string' && REAUTH_ERROR_CODES.has(code)) return true
  if (typeof message === 'string') {
    return /missing refresh token|login required|invalid_grant|consent required|interaction required/i.test(message)
  }
  return false
}

class FamilyPortalAPIClient {
  // Local dev: VITE_API_BASE_URL=/api so Vite proxies to family-portal-api (default https://localhost:8001)
  private static baseUrl: string = import.meta.env['VITE_API_BASE_URL'] ?? (import.meta.env['DEV'] ? '/api' : '')
  private getAccessToken?: () => Promise<string>
  private onUnauthorized?: () => void
  private static instance: FamilyPortalAPIClient | null = null

  private constructor(getAccessToken?: () => Promise<string>, onUnauthorized?: () => void) {
    this.getAccessToken = getAccessToken
    this.onUnauthorized = onUnauthorized
  }

  static getInstance(getAccessToken?: () => Promise<string>, onUnauthorized?: () => void): FamilyPortalAPIClient {
    if (!FamilyPortalAPIClient.instance) {
      FamilyPortalAPIClient.instance = new FamilyPortalAPIClient(getAccessToken, onUnauthorized)
    } else {
      if (getAccessToken) FamilyPortalAPIClient.instance.getAccessToken = getAccessToken
      if (onUnauthorized) FamilyPortalAPIClient.instance.onUnauthorized = onUnauthorized
    }
    return FamilyPortalAPIClient.instance
  }

  static resetInstance(): void {
    FamilyPortalAPIClient.instance = null
  }

  public setTokenGetter(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken
  }

  private async request(request: RequestStructure, withAuth: boolean = true): Promise<Response> {
    return Sentry.startSpan({ op: 'http.client', name: `${request.method} ${request.path}` }, async (span) => {
      span.setAttribute('http.method', request.method)
      span.setAttribute('http.url', `${FamilyPortalAPIClient.baseUrl}${request.path}`)
      span.setAttribute('with_auth', withAuth)

      const outgoingHeaders: Record<string, string> = request.headers || {}

      if (withAuth) {
        if (this.getAccessToken) {
          try {
            const accessToken = await this.getAccessToken()
            outgoingHeaders['Authorization'] = `Bearer ${accessToken}`
            span.setAttribute('auth_method', 'jwt')
          } catch (error) {
            console.error('[FamilyPortalAPIClient] Failed to get access token:', error)
            span.setAttribute('auth_error', true)
            const reauthRequired = isReauthRequiredError(error)
            span.setAttribute('reauth_required', reauthRequired)
            if (reauthRequired) {
              // Session is dead, so tear it down. Independent of suppressUnauthorizedRedirect,
              // which only tolerates a 401 response on a still-live session.
              this.onUnauthorized?.()
            } else {
              // Unexpected (e.g. transient SDK timeout): report it, but keep the session.
              Sentry.captureException(error, {
                tags: { operation: 'auth.getAccessToken', path: request.path },
              })
            }
            // Without a token we can't attach an Authorization header, so never fall through
            // to fetch(): an unauthenticated request only yields an unlogged backend 401.
            return new Response(null, { status: 401, statusText: 'Unauthorized' })
          }
        } else {
          console.warn('[FamilyPortalAPIClient] Auth requested but no token getter available')
        }
      }

      let processedBody: string | undefined
      if (request.body !== undefined && request.body !== null) {
        processedBody = typeof request.body === 'string' ? request.body : JSON.stringify(request.body)
        if (typeof request.body !== 'string') {
          outgoingHeaders['Content-Type'] = 'application/json'
        }
      }

      try {
        const response = await fetch(`${FamilyPortalAPIClient.baseUrl}${request.path}`, {
          method: request.method,
          headers: outgoingHeaders,
          body: processedBody,
          signal: request.signal,
        })
        span.setAttribute('http.status_code', response.status)
        if (response.status === 401 && withAuth && !request.suppressUnauthorizedRedirect) {
          this.onUnauthorized?.()
        }
        return response
      } catch (error) {
        // AbortError is expected when callers intentionally cancel in-flight requests
        // during route changes, unmounts, or React Strict Mode remounts.
        if (error instanceof DOMException && error.name === 'AbortError') {
          span.setAttribute('http.aborted', true)
          throw error
        }

        span.setAttribute('error', true)
        if (error instanceof Error) {
          span.setAttribute('error.message', error.message)
        }
        // Opaque network failures are an operational condition, not a program error.
        // The span already records them and traces are sampled at 100%, so querying
        // http.network_error keeps the rate signal without raising an issue that
        // groups badly, carries no actionable cause, and pages on-call.
        if (isOpaqueNetworkFailure(error)) {
          span.setAttribute('http.network_error', true)
        } else {
          Sentry.captureException(error, {
            tags: {
              operation: 'http.client',
              method: request.method,
              path: request.path,
            },
          })
        }
        throw error
      }
    })
  }

  public async get(
    path: string,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal,
    suppressUnauthorizedRedirect?: boolean
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.GET, path, headers, signal, suppressUnauthorizedRedirect }, withAuth)
  }

  public async post(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal,
    /**
     * Let the caller own a 401 on a STILL-LIVE session instead of tearing the
     * session down. A genuinely dead session is unaffected: the reauth-required
     * branch above calls `onUnauthorized` regardless of this flag. Needed for
     * POSTs that hold unrecoverable client state across the response — the HRC
     * consent calls keep an in-memory Idempotency-Key that a logout redirect
     * would destroy, stranding a request that may already have committed.
     */
    suppressUnauthorizedRedirect?: boolean
  ): Promise<Response> {
    return this.request(
      { method: HTTP_METHOD.POST, path, body, headers, signal, suppressUnauthorizedRedirect },
      withAuth
    )
  }

  public async put(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.PUT, path, body, headers, signal }, withAuth)
  }

  public async patch(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.PATCH, path, body, headers, signal }, withAuth)
  }

  public async delete(
    path: string,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.DELETE, path, headers, signal }, withAuth)
  }
}

export default FamilyPortalAPIClient
